import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click. Always opens the quick-add (Quick View) modal - the trigger
   * never adds to cart directly; the modal owns the real add-to-cart submission.
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Subscription widgets and personalization inputs only run on the product page, so these
    // products navigate there instead of opening the quick-add modal.
    if (this.dataset.usesSellingPlans === 'true' || this.dataset.requiresPersonalization === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let quickAddContent = this.#cachedContent.get(currentUrl);

    if (!quickAddContent) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const contentElement = html.querySelector('[data-quick-add-content]');
        if (contentElement) {
          // Cache the cloned element to avoid modifying the original
          quickAddContent = /** @type {Element} */ (contentElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, quickAddContent);
        }
      }
    }

    if (quickAddContent) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (quickAddContent.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(quickAddContent);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const details = dialogComponent.querySelector('.quick-add-content__details');
    const media = dialogComponent.querySelector('.quick-add-content__media');
    details?.scrollTo({ top: 0, behavior: 'instant' });
    media?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Morphs the fetched quick-add content into the modal.
   * @param {Element} quickAddContent - The quick-add content element
   */
  async updateQuickAddModal(quickAddContent) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!quickAddContent || !modalContent) return;

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = quickAddContent.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    // morph() defaults to childrenOnly: true (assets/morph.js) - it deliberately never touches the
    // target ROOT's own attributes, only its children, so the root keeps whatever identity the
    // caller gave it. quickAddContent's root carries the `quick-add-content` class that every rule
    // in quick-add-content-styles.liquid is scoped to (the two-column desktop layout, gallery
    // background, price/variant-picker color overrides, etc.) - without copying it across here,
    // none of that CSS would ever match anything in the live DOM.
    for (const className of quickAddContent.classList) {
      modalContent.classList.add(className);
    }

    morph(modalContent, quickAddContent);

    this.#syncVariantSelection(modalContent);

    // morph() patches the existing <quick-add-gallery> in place rather than recreating it, so its
    // JS-side #index (and the arrows/dots/thumbnails it drives) would otherwise keep whatever
    // value it had for the previously viewed product instead of picking up the fresh markup's own
    // slide 0.
    const gallery = /** @type {QuickAddGallery | null} */ (modalContent.querySelector('quick-add-gallery'));
    gallery?.resetToActiveSlide();
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @type {{ startY: number, currentY: number, dragging: boolean } | null} */
  #drag = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    /*
      pointermove/pointerup/pointercancel aren't in assets/component.js's global `on:*` event
      list (only pointerdown is), so the declarative on:pointermove/on:pointerup/on:pointercancel
      attributes on the drag handle (snippets/quick-add-modal.liquid) never actually fire -
      dragStart ran (via on:pointerdown, which IS registered) but dragMove/dragEnd never did, which
      is why the sheet only ever snapped fully open or fully closed instead of following the
      finger. Wired here with real addEventListener calls instead. Pointer capture (set in
      dragStart, on this same handle element) redirects these events to the handle regardless of
      where the pointer physically moves, so listening on the handle itself (not document) is
      enough.
    */
    const dragHandle = this.refs?.dragHandle;
    if (dragHandle) {
      dragHandle.addEventListener('pointermove', this.dragMove, { signal: this.#abortController.signal });
      dragHandle.addEventListener('pointerup', this.dragEnd, { signal: this.#abortController.signal });
      dragHandle.addEventListener('pointercancel', this.dragEnd, { signal: this.#abortController.signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * On a combined-listing variant swap, the fetched HTML is a different product's page. Both the
   * visible title (which doubles as `.view-product-title` - the same marker
   * blocks/_product-details.liquid uses, kept for consistency) and the separate "View more
   * details" link need to follow that swap, or they're left pointing at/naming the product the
   * modal was originally opened for.
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newTitleLink = /** @type {HTMLAnchorElement | null} */ (html.querySelector('.view-product-title a'));
        const titleLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement | null} */ (
          this.querySelector('.quick-add-content__view-details')
        );
        const newViewMoreDetailsLink = /** @type {HTMLAnchorElement | null} */ (
          html.querySelector('.quick-add-content__view-details')
        );

        if (newTitleLink && titleLink) {
          titleLink.href = newTitleLink.href;
          titleLink.textContent = newTitleLink.textContent;
        }

        if (newViewMoreDetailsLink && viewMoreDetailsLink) {
          viewMoreDetailsLink.href = newViewMoreDetailsLink.href;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const dialog = this.refs?.dialog;
    if (dialog) {
      dialog.style.transform = '';
      dialog.classList.remove('is-dragging');
    }
    this.#drag = null;

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  /**
   * Mobile bottom-sheet drag-to-dismiss. Desktop's side panel doesn't use this - the handle
   * itself is hidden above 749px (snippets/quick-add-modal-styles.liquid), so these only ever
   * fire from a touch/pointer drag on the sheet's own handle.
   * @param {PointerEvent} event
   */
  dragStart = (event) => {
    if (!isMobileBreakpoint()) return;
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    this.#drag = { startY: event.clientY, currentY: event.clientY, dragging: true };
    dialog.classList.add('is-dragging');
    event.target instanceof Element && event.target.setPointerCapture?.(event.pointerId);
  };

  /** @param {PointerEvent} event */
  dragMove = (event) => {
    if (!this.#drag?.dragging) return;
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    const offset = Math.max(0, event.clientY - this.#drag.startY);
    this.#drag.currentY = event.clientY;
    dialog.style.transform = `translateY(${offset}px)`;
  };

  /** @param {PointerEvent} event */
  dragEnd = (event) => {
    if (!this.#drag?.dragging) return;
    const dialog = this.refs?.dialog;
    const offset = Math.max(0, this.#drag.currentY - this.#drag.startY);
    this.#drag = null;

    if (!dialog) return;
    // Removing 'is-dragging' before clearing the inline transform (both below, on every path)
    // re-enables the CSS transition declared for that state (quick-add-modal-styles.liquid), so
    // the sheet eases back to translateY(0) instead of jumping there - closeDialog() (assets/dialog.js)
    // itself has no hook for an in-progress drag offset: it always plays its close keyframe from
    // a hard-coded translateY(0) 'from' state, which would otherwise cut straight from wherever
    // the finger let go to that in a single frame.
    dialog.classList.remove('is-dragging');
    event.target instanceof Element && event.target.releasePointerCapture?.(event.pointerId);

    const DISMISS_THRESHOLD_PX = 80;
    if (offset > DISMISS_THRESHOLD_PX) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      dialog.style.transform = '';
      if (prefersReducedMotion) {
        this.closeDialog();
      } else {
        dialog.addEventListener('transitionend', () => this.closeDialog(), { once: true });
      }
      return;
    }

    dialog.style.transform = '';
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * Bespoke quick-view gallery: a stage (main image + arrows + dots) and a thumbnail grid. Built
 * fresh rather than reusing assets/media-gallery.js's `<media-gallery>` component, because the
 * fetched page (for the "select a variant inside the open modal" flow) contains two products'
 * worth of media - the real PDP's own gallery and this fragment's hidden copy - and
 * media-gallery.js's own reactive update grabs "the first `<media-gallery>` in the document"
 * with no way to disambiguate which one dispatched the event. A from-scratch component sidesteps
 * that entirely: it never instantiates a `<media-gallery>` in the first place.
 */
class QuickAddGallery extends Component {
  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.refs?.track?.addEventListener('scroll', this.#handleTrackScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.refs?.track?.removeEventListener('scroll', this.#handleTrackScroll);
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.quick-add-content__slide')));
  }

  previous = () => this.#goTo(this.#index - 1);

  next = () => this.#goTo(this.#index + 1);

  /**
   * Re-syncs this instance's internal index to whichever slide the (freshly morphed-in) markup
   * itself marks active, rather than leaving it at its previous value for a different product.
   * See assets/quick-add.js's QuickAddComponent#updateQuickAddModal for why this is needed: morph()
   * patches this same custom element instance in place across products instead of recreating it.
   */
  resetToActiveSlide() {
    const index = this.#slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.#index = index === -1 ? 0 : index;
  }

  /** @param {Event} event */
  select = (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-index]') : null
    );
    const index = Number(button?.dataset.index);
    if (Number.isInteger(index)) this.#goTo(index);
  };

  /** @param {number} index */
  #goTo(index) {
    const slides = this.#slides;
    if (!slides.length) return;

    const next = ((index % slides.length) + slides.length) % slides.length;

    const track = this.refs?.track;
    if (track instanceof HTMLElement) {
      if (isMobileBreakpoint()) {
        slides[next]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      } else {
        // translateX() percentages resolve against the track's OWN width, which on desktop is
        // `slides.length * 100%` of the stage (see quick-add-content-styles.liquid's own comment
        // on why) - so moving to slide `next` out of N means shifting by `next / N`, not
        // `next * 100%` of the track's now much wider box.
        track.style.transform = `translateX(-${(next / slides.length) * 100}%)`;
      }
    }

    this.#setActiveIndex(next);
  }

  /**
   * Updates which slide/dot/thumbnail is marked active, without touching scroll position or the
   * desktop transform - the part of #goTo that's safe to call from a scroll listener (below),
   * where the user's own gesture (not us) already moved the track.
   * @param {number} index
   */
  #setActiveIndex(index) {
    const slides = this.#slides;
    this.#index = index;

    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));

    const dots = /** @type {HTMLElement | undefined} */ (this.refs?.dots);
    dots
      ?.querySelectorAll('.quick-add-content__dot')
      .forEach((dot, i) => dot.classList.toggle('is-active', i === index));

    const thumbnails = /** @type {HTMLElement | undefined} */ (this.refs?.thumbnails);
    thumbnails
      ?.querySelectorAll('.quick-add-content__thumbnail')
      .forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }

  /**
   * Mobile swipes the track natively (overflow-x: auto; see quick-add-content-styles.liquid) -
   * there's no click/call into #goTo to hook into, so the dots/thumbnails only stay in sync with
   * which slide is actually in view by watching scroll position directly, same approach the
   * prototype's own `onRailScroll` handler uses.
   * @param {Event} event
   */
  #handleTrackScroll = (event) => {
    const track = /** @type {HTMLElement} */ (event.currentTarget);
    const slides = this.#slides;
    if (!track.clientWidth || !slides.length) return;

    // Mobile slides are narrower than the track (the next photo peeks in), so step by one slide
    // plus the gap. The last photos can't scroll to the start edge, so the scroll end means last.
    const step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth;
    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    const index = atEnd ? slides.length - 1 : Math.round(track.scrollLeft / step);
    if (index !== this.#index) this.#setActiveIndex(index);
  };

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const mediaId = detail?.resource?.featured_media?.id ?? detail?.resource?.featured_image?.id;
        if (mediaId == null) return;

        const slides = this.#slides;
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index !== -1) this.#goTo(index);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('quick-add-gallery')) {
  customElements.define('quick-add-gallery', QuickAddGallery);
}
